import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react'
import ImmutablePropTypes from 'react-immutable-proptypes'
import PropTypes from 'prop-types'
import _get from 'lodash/get'
import _isFunction from 'lodash/isFunction'
import { List, Map, fromJS } from 'immutable'
import { useQuery, useMutation } from '@apollo/client'
import FormsyForm from 'formsy-react'
import { connect as connectRedux } from 'react-redux'
import { getBoundActions } from '../../../../actions/index'
import Sherpa, { TYPE_SMALL_BLUE } from '../../../Sherpa/index'
import { H5 } from '../../../Heading/index'
import { TextInput, Checkbox } from '../../../FormInput.v2/index'
import FeedbackBox, { FEEDBACK_BOX_TYPES } from '../../../FeedbackBox/index'
import { GET_PLAYLISTS, CREATE_PLAYLIST } from '../../../../services/playlist/query'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../../../services/apollo-graphql/index'
import {
  formatPlaylistInputErrors,
  getPlaylistNameByType,
  getUserPlaylistList,
  USER_PLAYLISTS_LIMIT,
} from '../../../../services/playlist/index'
import Button, { TYPES } from '../../../Button/index'
import { PLAY_LIST_CREATED_GA4_EVENT } from '../../../../services/event-tracking/index'
import InfoCircledIcon from '../../../Icon/icons/InfoCircled/index'

const MultiplePlaylistsAddModalGraphql = (props) => {
  const {
    setEventDefault,
    clearPlaylistItem,
    getPlaylistItem,
    dismissModal,
    staticText,
    createOnly,
    contentId,
    onChange,
    onCreate,
    uniqueId,
    authJwt,
    meta,
    uiLocation,
    uiElement,
    addToasty,
    tipText,
  } = props

  const [validForm, setValidForm] = useState(false)
  const [inputVisible, setInputVisible] = useState(false)

  const titleTextKey = createOnly ? 'titleCreate' : 'myPlaylists'
  const metaPlaylists = meta.get('playlists', List())

  const { data, refetch: refetchPlaylists } = useQuery(GET_PLAYLISTS, {
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: {
      jwt: authJwt,
    },
  })

  const playlistsList = _get(data, ['playlistList', 'playlists'], []) || []
  const { userPlaylists, customPlaylistsCount } = getUserPlaylistList(playlistsList)
  const playlists = fromJS(userPlaylists) || List()
  const canCreateCustomPlaylist = customPlaylistsCount < USER_PLAYLISTS_LIMIT

  const [
    createPlaylistMutation,
    { loading: creatingPlaylist, error: createPlaylistError },
  ] = useMutation(CREATE_PLAYLIST, {
    context: {
      jwt: authJwt,
    },
  })

  const createPlaylist = (playlistName) => {
    return createPlaylistMutation({
      variables: {
        playlistName,
      },
    })
  }

  const handleChange = useCallback((checked, playlist) => {
    if (_isFunction(onChange)) {
      onChange({
        playlistId: playlist.get('id'),
        playlistType: playlist.get('playlistType'),
        playlistName: getPlaylistNameByType(playlist, staticText),
        action: checked ? 'add' : 'remove',
        callback: playlist.get('id') ? null : refetchPlaylists,
      })
    }
    dismissModal()
  }, [contentId, playlists])

  const onSubmit = async ({ playlistName }) => {
    const response = await createPlaylist(playlistName)

    if (!createPlaylistError) {
      const playlistData = _get(response, ['data', 'playlistCreate', 'playlist'], {}) || {}
      const playlist = fromJS(playlistData) || Map()

      // GA4 event
      setEventDefault(PLAY_LIST_CREATED_GA4_EVENT.mergeDeep({
        playlist_name: getPlaylistNameByType(playlist, staticText),
        playlist_type: 'Custom',
        ui_element: uiElement,
        ui_location: uiLocation,
      }))

      addToasty(staticText.get('successMessage'))

      if (_isFunction(onCreate)) {
        onCreate(playlist)
      } else {
        await refetchPlaylists()
      }

      if (!createOnly) {
        handleChange(true, playlist)
      } else {
        dismissModal()
      }
    }
  }

  const submitButton = useMemo(() => {
    if (!canCreateCustomPlaylist) {
      return null
    }

    if (!inputVisible && !createOnly) {
      return null
    }

    if (creatingPlaylist) {
      return <Sherpa type={TYPE_SMALL_BLUE} className={['playlist-empty__sherpa']} />
    }

    return (
      <Button
        type={TYPES.PRIMARY}
        submit
        disabled={!validForm}
        stacked
        className="multiple-playlists-add-modal__button"
      >
        {staticText.get('buttonSave')}
      </Button>
    )
  }, [
    inputVisible,
    createOnly,
    creatingPlaylist,
    validForm,
    staticText,
    canCreateCustomPlaylist,
  ])

  const checkboxes = useMemo(() => {
    if (createOnly) {
      return null
    }

    const sortedPlaylists = playlists.map((playlist) => {
      const type = playlist.get('playlistType')
      const name = getPlaylistNameByType(playlist, staticText)
      const inPlaylist = !!metaPlaylists.find((metaPl) => metaPl.get('playlistType') === type)
      return {
        type,
        name,
        inPlaylist,
        playlist,
      }
    }).sort((a, b) => Number(b.inPlaylist) - Number(a.inPlaylist))

    return sortedPlaylists.map(({
      type,
      name,
      inPlaylist,
      playlist,
    }) => (
      <Checkbox
        key={type}
        label={name}
        onChange={(_, checked) => handleChange(checked, playlist)}
        value={inPlaylist}
        name={type}
        block
      />
    ))
  }, [
    createOnly,
    playlists,
    metaPlaylists,
    staticText,
    handleChange,
    getPlaylistNameByType,
  ])

  useEffect(() => {
    // TODO getPlaylistItem uses REST endpoint, should be switched to graphql
    if (contentId) {
      getPlaylistItem({
        componentId: uniqueId,
        contentId,
      })
    }
    return () => {
      clearPlaylistItem({
        componentId: uniqueId,
        contentId,
      })
    }
  }, [uniqueId, contentId])

  return (
    <div className="multiple-playlists-add-modal">
      <FormsyForm
        className="multiple-playlists-add-modal"
        onValid={() => setValidForm(true)}
        onInvalid={() => setValidForm(false)}
        onValidSubmit={onSubmit}
      >
        <H5>{staticText.get(titleTextKey)}</H5>
        {!createOnly
          ? <div className="multiple-playlists-add-modal__list">
            {playlists.size && meta.size
              ? checkboxes
              : <Sherpa type={TYPE_SMALL_BLUE} className={['multiple-playlists-add-modal__sherpa']} />
            }
            </div>
          : null
        }
        {createPlaylistError && <FeedbackBox type={FEEDBACK_BOX_TYPES.ERROR}>
            {staticText.get('otherNameMessage')}
          </FeedbackBox>
        }
        {!canCreateCustomPlaylist && (inputVisible || createOnly)
          && <FeedbackBox type={FEEDBACK_BOX_TYPES.INFO}>
            {staticText.get('customPlaylistsLimitMessage')}
          </FeedbackBox>
        }
        {(inputVisible || createOnly) && canCreateCustomPlaylist && <TextInput
            name="playlistName"
            label={staticText.get('placeholder')}
            validations={{
              maxLength: 40,
            }}
            validationErrors={{
              maxLength: staticText.get('inputMaxLengthMessage'),
            }}
            validationErrorFormat={formatPlaylistInputErrors}
            maxLength={100}
            autocomplete="off"
            value=""
            required
          />
        }
        {(!inputVisible && !createOnly) && <Button
            type={TYPES.SECONDARY}
            onClick={() => setInputVisible(true)}
            stacked
            className="multiple-playlists-add-modal__button"
          >
            {staticText.get('buttonCreate')}
          </Button>
        }
        {submitButton}
      </FormsyForm>
      {tipText
        && <div className="multiple-playlists-add-modal__tip">
          <InfoCircledIcon size={16} />
          <p className="multiple-playlists-add-modal__tip-text">{tipText}</p>
        </div>
      }
    </div>
  )
}

MultiplePlaylistsAddModalGraphql.propTypes = {
  staticText: ImmutablePropTypes.map.isRequired,
  authJwt: PropTypes.string.isRequired,
  meta: ImmutablePropTypes.map,
  createOnly: PropTypes.bool,
  uniqueId: PropTypes.string,
  contentId: PropTypes.number,
  onChange: PropTypes.func,
  onCreate: PropTypes.func,
}

MultiplePlaylistsAddModalGraphql.defaultProps = {
  createOnly: false,
}

export default connectRedux(
  (state, { contentId }) => {
    return {
      staticText: state.staticText.getIn(['data', 'multiplePlaylistsAddModal', 'data'], Map()),
      meta: state.playlist.getIn(['items', contentId, 'data'], Map()),
      authJwt: state.auth.get('jwt'),
    }
  },
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      addToasty: actions.toasty.addToasty,
      clearPlaylistItem: actions.playlist.clearPlaylistItem,
      getPlaylistItem: actions.playlist.getPlaylistItem,
      dismissModal: actions.dialog.dismissModal,
      setEventDefault: actions.eventTracking.setEventDefault,
    }
  },
)(MultiplePlaylistsAddModalGraphql)
